import SparkMD5 from 'spark-md5';
import { createMD5 } from 'hash-wasm';
import EventEmitter from 'event-emitter';
const WASM_MD5 = typeof WebAssembly !== 'undefined'; // 是否使用wasm计算md5
const CHUNK_SIZE = 4 * 1024 * 1024; // Read in chunks of 2MB
const MD5_SIZE_LIMIT = 1024 * 1024 * 256;

/**
 * 生成文件MD5的类
 *
 * @export
 * @class Spark
 * @author zx
 */
export default class MD5 {
  constructor() {
    EventEmitter(this);
    this.useWasmMd5 = WASM_MD5;
    this.file = null;
    this.progress = 0;
    this.currentPos = 0;
    this.pause = true;
    this.hash = null;
    this.reader = null;
  }

  /**
   * 计算md5
   * @param {*} md5Fn
   * @param {*} progressFn
   */
  async createMD5(file) {
    await this.setFile(file);
    this.pause = false;
    this._readerLoad();
    this.resumeSparkMD5();
    return new Promise(function (resolve) {
      this.once('md5', function (info) {
        resolve(info);
      });
    });
  }

  /**
   * 暂停md5计算
   */
  pauseSparkMD5() {
    this.pause = true;
  }

  /**
   * 恢复md5计算
   */
  resumeSparkMD5() {
    this.pause = false;
    this._readerLoad();
  }

  /**
   * 设置文件
   */
  async setFile(file) {
    if (this.file === file) return;
    this.file = file;
    this.progress = 0;
    this.currentPos = 0;
    this.pause = true;
    if (this.hash && this.hash.destroy) this.hash.destroy();

    // md5存储在sessionStorage可以不用再次计算，前提是文件是一样的
    const md5 = this._getStoreMd5(file);
    if (md5) {
      this.file.md5 = md5;
      setTimeout(() => this.emit('md5', { md5, file, progress: 1 }), 100);
      return;
    }

    this.hash = await this._getHash();
    this.reader = new FileReader();
    const self = this;

    this.reader.onloadend = function (e) {
      self._readerLoad(e.target.result);
    };

    this.reader.onerror = function (err) {
      this.pause = true;
      self.emit('error', err);
    };
  }

  destroy() {}

  /**
   * md5分片计算回调
   */
  _readerLoad(chunk) {
    const self = this;
    function loadNext(file, start) {
      if (!self.file) return;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      self.currentPos = end;
      try {
        self.reader.readAsArrayBuffer(file.slice(start, end));
      } catch (e) {
        self.pause = true;
        self.emit('error', e);
      }
    }

    if (this.file && this.file.md5) {
      this.emit('md5', { md5: this.file.md5, file: this.file, progress: 1 });
      this._setStoreMd5(this.file, this.file.md5);
      return;
    }

    // 开始加载文件和计算
    if (!chunk) return loadNext(this.file, this.currentPos);

    this._appedData(chunk);
    this.progress = this.currentPos / this.file.size;
    this.emit('progress', { file: this.file, progress: this.progress });

    if (this.currentPos >= this.file.size) {
      const md5 = this._digest();
      this.emit('md5', { md5: md5, file: this.file, progress: 1 });
      this._setStoreMd5(this.file, md5);
      return;
    }

    // 暂停计算md5
    if (this.pause) return;
    // setTimeout(loadNext, 100);
    loadNext(this.file, this.currentPos);
  }

  async _getHash() {
    if (this.useWasmMd5) {
      const hash = await createMD5();
      hash.init();
      return hash;
    } else {
      return new SparkMD5.ArrayBuffer();
    }
  }

  _appedData(data) {
    // Append array buffer
    if (this.useWasmMd5) {
      this.hash.update(new Uint8Array(data));
    } else {
      this.hash.append(data);
    }
  }

  _getFileId(file) {
    if (!file) return;
    if (!file.size) return;
    if (!file.name) return;
    const lastModified = file.lastModified ? file.lastModified : file.lastModifiedDate ? +file.lastModifiedDate : 0;
    if (!lastModified) return;
    return ['vod', 'hash', file.size, lastModified].join(':');
  }

  _getStoreMd5(file) {
    if (typeof sessionStorage === 'undefined') return;
    if (!file) return;
    const id = this._getFileId(file);
    if (!id) return;
    if (file.size < MD5_SIZE_LIMIT) return;
    try {
      const md5 = sessionStorage.getItem(id);
      if (md5 && md5.length !== 32) return;
      return md5;
    } catch (orz) {
      /**/
    }
  }

  _setStoreMd5(file, md5) {
    if (typeof sessionStorage === 'undefined') return;
    if (!file) return;
    // 小文件md5容易算，而且被修改的概率大
    const id = this._getFileId(file);
    if (!id) return;
    if (file.size < MD5_SIZE_LIMIT) return;
    try {
      sessionStorage.setItem(id, md5);
    } catch (orz) {
      /**/
    }
  }

  _digest() {
    if (this.useWasmMd5) {
      return this.hash.digest('hex');
    }

    return this.hash.end();
  }
}
